import makeRequest from 'utils/makeRequest'
import { ENDPOINTS } from 'config/apiEndpoints'
import INewsResponse from 'models/News/INews'
import INewsCountResponse from 'models/News/INewsCount'
import INewsCategoryResponse from 'models/News/INewsCategory'
import INewsTagResponse from 'models/News/INewsTag'
import INewsDetailsResponse from 'models/News/INewsDetails'

export const newsService = {
  getLatestNews: async (): Promise<INewsResponse> => {
    try {
      const response: INewsResponse = await makeRequest(
        'GET',
        ENDPOINTS.News.GetLatestNews
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get latest news data failed')
    }
  },
  getNewsCount: async (
    searchTerm: string,
    categoryId: string,
    tagId: string,
    isAr: boolean
  ): Promise<INewsCountResponse> => {
    try {
      const searchTermFilter = searchTerm
        ? isAr
          ? `[title.ar match "*${searchTerm}*"]`
          : `[title.en match "*${searchTerm}*"]`
        : ''
      const categoryFilter =
        categoryId && categoryId !== '0'
          ? `[category->_id match "${categoryId}"]`
          : ''
      const tagFilter =
        tagId && tagId !== '0' ? `["${tagId}" in tags[]->_id]` : ''

      const response: INewsCountResponse = await makeRequest(
        'GET',
        `${ENDPOINTS.News.GetNewsCountPrefix}${searchTermFilter}${categoryFilter}${tagFilter}${ENDPOINTS.News.GetNewsCountSuffix}`
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get news count failed')
    }
  },
  getPaginatedNews: async (
    from: number,
    to: number,
    searchTerm: string,
    categoryId: string,
    tagId: string,
    isAr: boolean
  ): Promise<INewsResponse> => {
    try {
      const searchTermFilter = searchTerm
        ? isAr
          ? `[title.ar match "*${searchTerm}*"]`
          : `[title.en match "*${searchTerm}*"]`
        : ''
      const categoryFilter =
        categoryId && categoryId !== '0'
          ? `[category->_id match "${categoryId}"]`
          : ''
      const tagFilter =
        tagId && tagId !== '0' ? `["${tagId}" in tags[]->_id]` : ''

      const query = `*[_type == "news"][!(_id in path("drafts.**"))]${searchTermFilter}${categoryFilter}${tagFilter} | order(publishedAt desc)[${from}..${to}]{ 
        "id": _id, 
        "updatedAt": _updatedAt, 
        "titleAr": title.ar, 
        "titleEn": title.en, 
        "slugAr": slug.ar.current, 
        "slugEn": slug.en.current, 
        "image": image.asset->url,
        "descriptionAr": description.ar,
        "descriptionEn": description.en,
        "featured": featured, 
        "publishedAt": publishedAt,
        author->{ 
          "id": _id, 
          "nameAr": name.ar, 
          "nameEn": name.en, 
          "photo": photo.asset->url
        },
        category->{ 
          "id": _id, 
          "nameAr": name.ar, 
          "nameEn": name.en 
        },
        tags[]->{ 
          "id": _id,
        }
      }`

      const response: INewsResponse = await makeRequest(
        'GET',
        `${ENDPOINTS.News.GetPaginatedNews}?query=${query}`
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get news failed')
    }
  },
  getNewsCategories: async (): Promise<INewsCategoryResponse> => {
    try {
      const response: INewsCategoryResponse = await makeRequest(
        'GET',
        ENDPOINTS.News.GetNewsCategories
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get news categories failed')
    }
  },
  getNewsTags: async (): Promise<INewsTagResponse> => {
    try {
      const response: INewsTagResponse = await makeRequest(
        'GET',
        ENDPOINTS.News.GetNewsTags
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get news tags failed')
    }
  },
  getNewsDetails: async (slug: string): Promise<INewsDetailsResponse> => {
    try {
      const response: INewsDetailsResponse = await makeRequest(
        'GET',
        `${ENDPOINTS.News.GetNewsDetailsPrefix}${slug}${ENDPOINTS.News.GetNewsDetailsSuffix}`
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get news details failed')
    }
  },
  getNewsByTags: async (tags: string[]): Promise<INewsResponse> => {
    try {
      const response: INewsResponse = await makeRequest(
        'GET',
        `${ENDPOINTS.News.GetNewsByTagsPrefix}${JSON.stringify(tags)}${ENDPOINTS.News.GetNewsByTagsSuffix}`
      )

      return response
    } catch (error) {
      console.error(error)
      throw new Error('Get news failed')
    }
  },
}
